import './BlackFriday2025.sass';

import { FC, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Checkbox, Form, FormItem, Header, Input, ScrollView } from '$uikit';
import { useCurrentUserInfo, useTranslator, useWikiLink } from '$hooks';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import {
  BlackFriday2025Meta,
  PromotionConsentResponse,
} from './BlackFriday2025.interface';
import { goTo } from '$navigation/helper';
import { FEED } from '$shared/constants/pages';
import { mainActions } from '$store/main';
import { Truncate } from '$shared/components';

const CAMPAIGN = 'blackfriday2025';
const MIN_DISCOUNT = 20;
export const BlackFriday2025: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const currentUser = useCurrentUserInfo();
  const wikiLink = useWikiLink('/docs-and-terms/black-friday');

  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [discountPercent, setDiscountPercent] = useState('');
  const [hasConsent, setHasConsent] = useState(false);
  const [existingDiscount, setExistingDiscount] = useState<number | null>(null);
  const [isBanned, setIsBanned] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isVerifiedModel = currentUser?.verified === 1 || currentUser?.verified === 2;

  useEffect(() => {
    const loadConsent = async () => {
      try {
        setLoading(true);
        const response = await Api.get<any, PromotionConsentResponse>(
          `/promotion_consent/${CAMPAIGN}`,
        );

        if (response.consent) {
          setHasConsent(true);
          const meta: BlackFriday2025Meta = JSON.parse(response.consent.meta);
          setAgreed(meta.agreed);
          setExistingDiscount(meta.discount_percent);
          setIsBanned(meta.banned);
        }
      } catch (e: any) {
        if (e?.code !== 'not_found') {
          dispatch(toastActions.setToastFail(e?.message || 'internal error'));
        }
      } finally {
        setLoading(false);
      }
    };

    if (isVerifiedModel) {
      loadConsent();
    } else {
      setLoading(false);
    }
  }, [isVerifiedModel, dispatch]);

  const handleSubmit = useCallback(async () => {
    if (!agreed) {
      dispatch(toastActions.setToastFail(t('black_friday_error_agree')));
      return;
    }

    const discount = parseInt(discountPercent, 10);

    if (isNaN(discount) || discount < MIN_DISCOUNT || discount > 100) {
      dispatch(toastActions.setToastFail(`${MIN_DISCOUNT}-100%`));
      return;
    }

    try {
      setSubmitting(true);
      dispatch(toastActions.setToastLoading());

      const meta: BlackFriday2025Meta = {
        agreed: true,
        discount_percent: discount,
        banned: false,
      };

      await Api.post(`/promotion_consent/${CAMPAIGN}/submit`, meta);

      dispatch(toastActions.setToastSuccess());
      dispatch(mainActions.setBlackFriday2025Enabled(true));
      setHasConsent(true);
      setExistingDiscount(discount);
    } catch (error: any) {
      dispatch(toastActions.setToastFail(error?.message || t('error')));
    } finally {
      setSubmitting(false);
    }
  }, [agreed, discountPercent, dispatch, t]);

  if (!isVerifiedModel) {
    return (
      <>
        <Header showBackButton>{t('black_friday_title')}</Header>
        <ScrollView>
          <div className="BlackFriday2025__accessDenied">
            <div className="BlackFriday2025__accessDeniedTitle">
              {t('black_friday_access_denied_title')}
            </div>
            <div className="BlackFriday2025__accessDeniedText">
              <Truncate text={t('black_friday_access_denied_text')} />
            </div>
          </div>
        </ScrollView>
      </>
    );
  }

  if (isBanned) {
    return (
      <>
        <Header showBackButton>{t('black_friday_title')}</Header>
        <ScrollView>
          <div className="BlackFriday2025__accessDenied">
            <div className="BlackFriday2025__accessDeniedTitle">
              {t('black_friday_banned_title')}
            </div>
            <div className="BlackFriday2025__accessDeniedText">
              <Truncate text={t('black_friday_banned_text')} />
            </div>
          </div>
        </ScrollView>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header showBackButton>{t('black_friday_title')}</Header>
        <ScrollView>
          <div className="BlackFriday2025__loading">{t('loading')}</div>
        </ScrollView>
      </>
    );
  }

  if (hasConsent && existingDiscount !== null) {
    return (
      <>
        <Header showBackButton>{t('black_friday_title')}</Header>
        <ScrollView>
          <div className="BlackFriday2025__container">
            <div className="BlackFriday2025__successCard">
              <div className="BlackFriday2025__successTitleRow">
                <div className="BlackFriday2025__successIcon">🎉</div>
                <div className="BlackFriday2025__successTitle">
                  {t('black_friday_participating')}
                </div>
              </div>
              <div className="BlackFriday2025__successDiscount">{existingDiscount}%</div>
              <div className="BlackFriday2025__successText">
                <Truncate text={t('black_friday_participating_text')} />
              </div>
            </div>

            <div className="BlackFriday2025__infoBlock">
              <a href={wikiLink} target="_blank" rel="noopener noreferrer">
                {t('black_friday_rules_link')}
              </a>
            </div>

            <Button stretched onClick={() => goTo(FEED)}>
              {t('back')}
            </Button>
          </div>
        </ScrollView>
      </>
    );
  }

  return (
    <>
      <Header showBackButton>{t('black_friday_title')}</Header>
      <ScrollView
        footer={
          <Button stretched onClick={handleSubmit} disabled={submitting || hasConsent}>
            {t('black_friday_submit_button')}
          </Button>
        }
      >
        <div className="BlackFriday2025__container">
          <div className="BlackFriday2025__header">
            <div className="BlackFriday2025__titleRow">
              <div className="BlackFriday2025__emoji">🔥</div>
              <div className="BlackFriday2025__title">{t('black_friday_title')}</div>
            </div>
            <div className="BlackFriday2025__dates">{t('black_friday_dates')}</div>
          </div>

          <div className="BlackFriday2025__description">
            <Truncate text={t('black_friday_description')} />
          </div>

          <div className="BlackFriday2025__infoBlock">
            <a href={wikiLink} target="_blank" rel="noopener noreferrer">
              {t('black_friday_rules_link')}
            </a>
          </div>

          <Form>
            <FormItem title={t('black_friday_discount_label')}>
              <Input
                type="number"
                placeholder={`${MIN_DISCOUNT}-100%`}
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                disabled={hasConsent}
              />
            </FormItem>

            <FormItem minimize>
              <Checkbox checked={agreed} onClick={() => setAgreed(!agreed)}>
                {t('black_friday_agreement')}
              </Checkbox>
            </FormItem>
          </Form>

          <div className="BlackFriday2025__warnings">
            <div className="BlackFriday2025__warningItem">
              <div className="BlackFriday2025__warningDot"></div>
              <div className="BlackFriday2025__warningText">
                {t('black_friday_warning_exclusion')}
              </div>
            </div>
            <div className="BlackFriday2025__warningItem BlackFriday2025__warningItem--recommendation">
              <div className="BlackFriday2025__warningDot"></div>
              <div className="BlackFriday2025__warningText">
                {t('black_friday_warning_recommendation')}
              </div>
            </div>
            <div className="BlackFriday2025__warningItem">
              <div className="BlackFriday2025__warningDot"></div>
              <div className="BlackFriday2025__warningText">
                {t('black_friday_warning_immutable')}
              </div>
            </div>
            <div className="BlackFriday2025__warningItem">
              <div className="BlackFriday2025__warningDot"></div>
              <div className="BlackFriday2025__warningText">
                {t('black_friday_warning_notification')}
              </div>
            </div>
          </div>
        </div>
      </ScrollView>
    </>
  );
};
